import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  IRetryPolicy,
  LogLevel,
  RetryContext,
} from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseService } from '../base-service';
import {
  CommunicationAttachment,
  CommunicationReferential,
  HeadOfficeInfo,
  HeadOfficeInfoInput,
} from './models';

type AccessTokenFactory = () => Promise<string>;

export type CommunicationsHubConnectionFactory = (
  hubUrl: string,
  accessTokenFactory: AccessTokenFactory
) => HubConnection;

const RECONNECT_DELAYS_MS = [0, 2_000, 10_000, 30_000] as const;
const INITIAL_RECONNECT_DELAY_MS = 5_000;

class ContinuousRetryPolicy implements IRetryPolicy {
  nextRetryDelayInMilliseconds(retryContext: RetryContext): number {
    return RECONNECT_DELAYS_MS[Math.min(retryContext.previousRetryCount, RECONNECT_DELAYS_MS.length - 1)];
  }
}

export const COMMUNICATIONS_HUB_CONNECTION_FACTORY = new InjectionToken<CommunicationsHubConnectionFactory>(
  'COMMUNICATIONS_HUB_CONNECTION_FACTORY',
  {
    providedIn: 'root',
    factory: () => (hubUrl, accessTokenFactory) =>
      new HubConnectionBuilder()
        .withUrl(hubUrl, { accessTokenFactory })
        .withAutomaticReconnect(new ContinuousRetryPolicy())
        .configureLogging(environment.production ? LogLevel.Warning : LogLevel.Information)
        .build(),
  }
);

@Injectable({ providedIn: 'root' })
export class CommunicationsService extends BaseService {
  private readonly createHubConnection = inject(COMMUNICATIONS_HUB_CONNECTION_FACTORY);
  private readonly visibleInfosSubject = new BehaviorSubject<HeadOfficeInfo[]>([]);
  readonly visibleInfos$ = this.visibleInfosSubject.asObservable();

  private connection?: HubConnection;
  private connectionStartPromise?: Promise<void>;
  private started = false;
  private initialReconnectTimer?: ReturnType<typeof setTimeout>;
  private pollTimer?: ReturnType<typeof setInterval>;

  private get apiUrl(): string { return environment.configurators2d.baseApi; }

  async start(): Promise<void> {
    if (this.started || !this.current.vendor) return;
    this.started = true;
    await this.refreshInfos(false);
    if (!this.started || !this.current.vendor) return;
    this.pollTimer = setInterval(() => void this.refreshInfos(false), 30_000);
    await this.connectRealtime();
  }

  stop(): void {
    this.started = false;
    const connection = this.connection;
    this.connection = undefined;
    this.connectionStartPromise = undefined;
    if (this.initialReconnectTimer) clearTimeout(this.initialReconnectTimer);
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.initialReconnectTimer = undefined;
    this.pollTimer = undefined;
    this.visibleInfosSubject.next([]);

    if (connection) {
      connection.off('infoChanged');
      void connection.stop().catch(error => this.logRealtimeError('Impossible d’arrêter la connexion SignalR.', error));
    }
  }


  async refreshInfos(handleError = true): Promise<HeadOfficeInfo[]> {
    try {
      const infos = await this.get<HeadOfficeInfo[]>(this.apiUrl + 'HeadOfficeInfos', undefined, undefined, handleError);
      this.visibleInfosSubject.next(infos);
      return infos;
    } catch {
      return this.visibleInfosSubject.value;
    }
  }


  async getInfos(theme?: string): Promise<HeadOfficeInfo[]> {
    const params = theme ? this.buildHttpParamsFromObject({ theme }) : undefined;
    const infos = await this.get<HeadOfficeInfo[]>(this.apiUrl + 'HeadOfficeInfos', params);
    if (!theme) this.visibleInfosSubject.next(infos);
    return infos;
  }

  getInfo(id: string): Promise<HeadOfficeInfo> {
    return this.get<HeadOfficeInfo>(`${this.apiUrl}HeadOfficeInfos/${id}`);
  }

  async markInfoRead(id: string): Promise<void> {
    await this.put<void>(`${this.apiUrl}HeadOfficeInfos/${id}/read`);
    this.visibleInfosSubject.next(
      this.visibleInfosSubject.value.map(info => info.id === id ? { ...info, isRead: true } : info)
    );
  }

  getAdminInfos(): Promise<HeadOfficeInfo[]> {
    return this.get<HeadOfficeInfo[]>(this.apiUrl + 'HeadOfficeInfos/admin');
  }

  getAdminInfo(id: string): Promise<HeadOfficeInfo> {
    return this.get<HeadOfficeInfo>(`${this.apiUrl}HeadOfficeInfos/admin/${id}`);
  }

  createInfo(input: HeadOfficeInfoInput): Promise<HeadOfficeInfo> {
    return this.post<HeadOfficeInfo>(this.apiUrl + 'HeadOfficeInfos/admin', input);
  }

  updateInfo(id: string, input: HeadOfficeInfoInput): Promise<HeadOfficeInfo> {
    return this.put<HeadOfficeInfo>(`${this.apiUrl}HeadOfficeInfos/admin/${id}`, input);
  }

  setInfoActive(id: string, active: boolean): Promise<HeadOfficeInfo> {
    return this.put<HeadOfficeInfo>(`${this.apiUrl}HeadOfficeInfos/admin/${id}/active`, { active });
  }

  getReferential(): Promise<CommunicationReferential> {
    return this.get<CommunicationReferential>(this.apiUrl + 'HeadOfficeInfos/admin/referential');
  }

  async stageUpload(file: File): Promise<CommunicationAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.post<CommunicationAttachment>(this.apiUrl + 'CommunicationUploads/admin', formData);
  }

  deleteStagedUpload(id: string): Promise<void> {
    return this.delete<void>(`${this.apiUrl}CommunicationUploads/admin/${id}`);
  }

  private connectRealtime(): Promise<void> {
    if (!this.started || !this.current.vendor) return Promise.resolve();
    if (this.connectionStartPromise) return this.connectionStartPromise;

    const connection = this.connection ?? this.createRealtimeConnection();
    if (connection.state !== HubConnectionState.Disconnected) return Promise.resolve();

    this.connectionStartPromise = this.startConnection(connection);
    return this.connectionStartPromise;
  }

  private createRealtimeConnection(): HubConnection {
    const hubUrl = this.apiUrl.replace(/\/api\/?$/i, '/hubs/communications');
    const connection = this.createHubConnection(hubUrl, async () => {
      const vendor = this.current.vendor;
      if (!this.started || !vendor) return '';
      return (await vendor.accessToken()) ?? '';
    });

    connection.on('infoChanged', () => void this.refreshInfos(false));
    connection.onreconnected(() => {
      if (this.connection === connection && this.started) {
        void this.refreshInfos(false);
      }
    });
    connection.onclose(error => {
      if (this.connection !== connection || !this.started) return;
      this.scheduleInitialReconnect(error);
    });

    this.connection = connection;
    return connection;
  }

  private async startConnection(connection: HubConnection): Promise<void> {
    try {
      await connection.start();
    } catch (error) {
      if (this.connection === connection && this.started) this.scheduleInitialReconnect(error);
    } finally {
      if (this.connection === connection) this.connectionStartPromise = undefined;
    }
  }

  private scheduleInitialReconnect(error?: unknown): void {
    if (!this.started || this.initialReconnectTimer) return;
    this.logRealtimeError('Connexion SignalR indisponible, nouvelle tentative programmée.', error);
    this.initialReconnectTimer = setTimeout(() => {
      this.initialReconnectTimer = undefined;
      void this.connectRealtime();
    }, INITIAL_RECONNECT_DELAY_MS);
  }

  private logRealtimeError(message: string, error?: unknown): void {
    if (!environment.production) console.warn(message, error);
  }
}
